import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getStripeEnvironment } from "@/lib/stripe";

export type CheckoutOptions = {
  /** Human-readable price lookup key set in Stripe (e.g. "pro_monthly"). */
  priceId: string;
  successUrl?: string;
  cancelUrl?: string;
};

/**
 * Redirects the browser to a Stripe Checkout session for the given lookup key.
 * The signed-in user id is attached server-side via metadata + client_reference_id
 * so the webhook can link the resulting subscription back to the user.
 */
export function useStripeCheckout() {
  const [loading, setLoading] = useState(false);

  const openCheckout = async (options: CheckoutOptions) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("stripe-checkout", {
        body: {
          priceId: options.priceId,
          environment: getStripeEnvironment(),
          successUrl:
            options.successUrl || `${window.location.origin}/checkout/success`,
          cancelUrl: options.cancelUrl || `${window.location.origin}/pricing`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(String(data.error));
      const url = data?.url as string | undefined;
      if (!url) throw new Error("No checkout URL returned");
      window.location.assign(url);
    } finally {
      setLoading(false);
    }
  };

  return { openCheckout, loading };
}
